import { useState, useEffect } from 'react';
import posthog from 'posthog-js';

// Chrome fires `beforeinstallprompt` once, seconds after page load. Components
// mount far too late to catch it (e.g. the Settings install modal), so the
// event is captured here at module scope and stashed on `window`. main.jsx
// imports this module so the listener is registered before the event fires.
const PROMPT_READY_EVENT = 'ttt:pwa-prompt-ready';

if (typeof window !== 'undefined' && !window.__tttPwaCapture) {
  window.__tttPwaCapture = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    window.deferredPrompt = e;
    window.dispatchEvent(new Event(PROMPT_READY_EVENT));
  });
  window.addEventListener('appinstalled', () => {
    window.deferredPrompt = null;
    window.dispatchEvent(new Event(PROMPT_READY_EVENT));
  });
}

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(() => window.deferredPrompt || null);
  const [isInstallable, setIsInstallable] = useState(() => Boolean(window.deferredPrompt));

  useEffect(() => {
    const sync = () => {
      setDeferredPrompt(window.deferredPrompt || null);
      setIsInstallable(Boolean(window.deferredPrompt));
    };
    // The prompt may have arrived between first render and effect setup.
    sync();
    window.addEventListener(PROMPT_READY_EVENT, sync);
    return () => window.removeEventListener(PROMPT_READY_EVENT, sync);
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    posthog.capture('pwa_install_prompt_outcome', { outcome });

    setDeferredPrompt(null);
    setIsInstallable(false);
    window.deferredPrompt = null;
  };

  return { isInstallable, installApp };
};
